<template>
    <div>

        <Head :title="title" />
        <div class="max-w-full bg-white rounded-md shadow overflow-hidden">
            <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <text-input v-model="form.name" :error="form.errors.name" class="pb-8 pr-6 w-full lg:w-1/2"
                        :label="__('Name')" />
                    <select-input v-model="form.priority_id" :error="form.errors.priority_id"
                        class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Priority')">
                        <option value="">{{ __('Select Priority') }}</option>
                        <option v-for="priority in priorities" :key="priority.id" :value="priority.id">
                            {{ priority.name }}
                        </option>
                    </select-input>
                    <select-input v-model="form.status_id" :error="form.errors.status_id"
                        class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Status')">
                        <option value="">{{ __('Select Status') }}</option>
                        <option v-for="status in statuses" :key="status.id" :value="status.id">
                            {{ status.name }}
                        </option>
                    </select-input>

                    <text-input v-model="form.escalate_after_minutes" :error="form.errors.escalate_after_minutes"
                        class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Escalate After (minutes)')" type="number"
                        min="1" />
                    <select-input v-model="form.notification_method" :error="form.errors.notification_method"
                        class="pb-8 pr-6 w-full lg:w-1/2" :label="__('Notification Method')">
                        <option value="email">{{ __('Email') }}</option>
                        <option value="sms">{{ __('SMS') }}</option>
                        <option value="slack">{{ __('Slack') }}</option>
                    </select-input>

                    <!-- Users Multiselect Dropdown -->
                    <div class="pb-8 pr-6 w-full lg:w-1/2">
                        <label class="form-label">{{ __('Escalate To Users') }}</label>
                        <multiselect v-model="form.escalate_to_users" :options="userOptions" :multiple="true"
                            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                            placeholder="Select users..." label="name" track-by="id" :preselect-first="false"
                            :searchable="true" :loading="false" :internal-search="true" :max-height="200"
                            :show-no-results="true" :hide-selected="true" class="multiselect-custom">
                            <template #selection="{ values, search, isOpen }">
                                <span v-if="values.length && !isOpen" class="multiselect__single">
                                    {{ values.length }} user{{ values.length === 1 ? '' : 's' }} selected
                                </span>
                            </template>
                            <template #option="{ option }">
                                <div class="option__desc">
                                    <span class="option__title">{{ option.name }}</span>
                                    <span v-if="option.role" class="option__small">{{ option.role.name }}</span>
                                </div>
                            </template>
                            <template #noResult>
                                <span>No users found</span>
                            </template>
                        </multiselect>
                        <div v-if="form.errors.escalate_to_users" class="form-error">
                            {{ form.errors.escalate_to_users }}
                        </div>
                    </div>

                    <!-- Roles Multiselect -->
                    <div v-if="roles && roles.length" class="pb-8 pr-6 w-full lg:w-1/2">
                        <label class="form-label">{{ __('Escalate To Roles') }}</label>
                        <multiselect v-model="form.escalate_to_roles" :options="roleOptions" :multiple="true"
                            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                            placeholder="Select roles..." label="name" track-by="id" :preselect-first="false"
                            :searchable="true" :loading="false" :internal-search="true" :max-height="200"
                            :show-no-results="true" :hide-selected="true" class="multiselect-custom">
                            <template #selection="{ values, search, isOpen }">
                                <span v-if="values.length && !isOpen" class="multiselect__single">
                                    {{ values.length }} role{{ values.length === 1 ? '' : 's' }} selected
                                </span>
                            </template>
                            <template #noResult>
                                <span>No roles found</span>
                            </template>
                        </multiselect>
                        <div v-if="form.errors.escalate_to_roles" class="form-error">
                            {{ form.errors.escalate_to_roles }}
                        </div>
                    </div>

                    <div class="pb-8 pr-6 w-full lg:w-1/2 flex items-center">
                        <input id="is_active" v-model="form.is_active" type="checkbox" class="mr-2" />
                        <label for="is_active">{{ __('Active') }}</label>
                    </div>
                </div>
                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <button class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
                        {{ __('Delete Escalation Matrix') }}
                    </button>
                          <Link
                class="btn-gray m-6"
                :href="route('escalation.cancel')"
                method="post"
                as="button"
                type="button"
                preserve-scroll
            >
                {{ __('Cancel') }}
            </Link>
                    <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
                        {{ __('Update Escalation Matrix') }}
                    </loading-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'


export default {
    components: {
        Head,
        Link,
        LoadingButton,
        SelectInput,
        TextInput,
        Multiselect,
    },
    layout: Layout,
    props: {
        matrix: Object,
        users: Array,
        roles: Array,
        priorities: Array,
        statuses: Array,
        title: String,
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                name: this.matrix.name,
                priority_id: this.matrix.priority_id,
                status_id: this.matrix.status_id,
                escalate_after_minutes: this.matrix.escalate_after_minutes,
                escalate_to_roles: this.matrix.escalate_to_roles || [],
                escalate_to_users: this.matrix.escalate_to_users || [],
                notification_method: this.matrix.notification_method,
                is_active: this.matrix.is_active,
            }),
        }
    },
    computed: {
        userOptions() {
            return this.users.map(user => ({
                id: user.id,
                name: `${user.first_name} ${user.last_name}`,
                role: user.role,
            }))
        },
        roleOptions() {
            return this.roles ? this.roles.map(role => ({
                id: role.id,
                name: role.name,
            })) : []
        },
    },
    mounted() {
        // Convert existing user/role IDs to objects for multiselect
        if (this.matrix.escalate_to_users && this.matrix.escalate_to_users.length > 0) {
            this.form.escalate_to_users = this.matrix.escalate_to_users.map(userId => {
                const user = this.users.find(u => u.id === userId)
                return user ? {
                    id: user.id,
                    name: `${user.first_name} ${user.last_name}`,
                    role: user.role,
                } : null
            }).filter(Boolean)
        }

        if (this.roles && this.matrix.escalate_to_roles && this.matrix.escalate_to_roles.length > 0) {
            this.form.escalate_to_roles = this.matrix.escalate_to_roles.map(roleId => {
                const role = this.roles.find(r => r.id === roleId)
                return role ? {
                    id: role.id,
                    name: role.name,
                } : null
            }).filter(Boolean)
        }
    },
    methods: {
        update() {
            // Extract just the IDs for the backend
            const formData = {
                ...this.form.data(),
                escalate_to_users: this.form.escalate_to_users.map(user => user.id),
                escalate_to_roles: this.form.escalate_to_roles.map(role => role.id),
            }

            this.form.transform(() => formData).put(this.route('escalation.update', this.matrix.id))
        },
        destroy() {
            if (confirm('Are you sure you want to delete this escalation matrix?')) {
                this.$inertia.delete(this.route('escalation.destroy', this.matrix.id), {
                    onSuccess: () => {
                        this.$toast.success('Escalation matrix deleted successfully!');
                        // Optional: Redirect after deletion
                        setTimeout(() => {
                            this.$inertia.visit(this.route('escalation.index'));
                        }, 1500);
                    },
                    onError: () => {
                        this.$toast.error('Failed to delete escalation matrix!');
                    }
                });
            }
        },
    },
}
</script>

<style>
/* Custom multiselect styling */
.multiselect-custom {
    min-height: 40px;
}

.multiselect-custom .multiselect__tags {
    min-height: 40px;
    padding: 8px 40px 0 8px;
    border-radius: 4px;
    border: 1px solid #d1d5db;
}

.multiselect-custom .multiselect__tags:focus-within {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 1px #3b82f6;
}

.multiselect-custom .multiselect__placeholder {
    color: #6b7280;
    display: inline-block;
    margin-bottom: 10px;
    padding-top: 2px;
}

.multiselect-custom .multiselect__tag {
    background: #3b82f6;
    color: white;
    border-radius: 4px;
    padding: 2px 8px;
    margin-bottom: 5px;
    margin-right: 5px;
}

.multiselect-custom .multiselect__tag-icon:after {
    color: #bfdbfe;
}

.multiselect-custom .multiselect__tag-icon:focus,
.multiselect-custom .multiselect__tag-icon:hover {
    background: #1e40af;
}

.multiselect-custom .multiselect__option--highlight {
    background: #3b82f6;
    color: white;
}

.multiselect-custom .multiselect__option--selected {
    background: #f3f4f6;
    color: #374151;
    font-weight: 500;
}

.multiselect-custom .multiselect__option--selected.multiselect__option--highlight {
    background: #dc2626;
    color: white;
}

.option__desc {
    display: flex;
    flex-direction: column;
}

.option__title {
    font-weight: 500;
    color: #374151;
}

.option__small {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 2px;
}

.form-label {
    display: block;
    font-weight: 500;
    color: #374151;
    margin-bottom: 8px;
    font-size: 0.875rem;
}

.form-error {
    color: #dc2626;
    font-size: 0.875rem;
    margin-top: 4px;
}
</style>
